/**
 * #976: the card annotation HTTP client. Strictly validates every reply, drops
 * any reply that lands after the auth session changed (login, logout, account
 * switch), never retries, never polls and never stores anything locally.
 */
import type { CardSource, CardSourceAssignment, CardSourceCreated } from '../cardSources/types';
import type { CardNoteRecord } from '../cardNotes/types';
import { TRADER_VIEW_SCOPE, type CardAnnotationTransport, type CardScopeResult, type CardTarget, type CardUnavailableTarget, type CardVenue } from './types';

const BASE = '/api/card-annotations';
const TIMEOUT_MS = 15_000;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SCOPE = /^cas1\.(?:kalshi|polymarket)(?:\+(?:kalshi|polymarket))?\.[A-Za-z0-9_-]{43}$/;
const CODE = /^CARD_[A-Z_]{1,60}$/;
const ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?Z$/;
const VENUES: readonly CardVenue[] = ['kalshi', 'polymarket'];

export class CardAnnotationClientError extends Error {
  constructor(readonly code: string, readonly status: number | null = null, readonly body: unknown = null) {
    super(code);
    this.name = 'CardAnnotationClientError';
  }
}

const bad = (): never => { throw new CardAnnotationClientError('CARD_CLIENT_INVALID_RESPONSE'); };
const object = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : bad();

/** Stable identity of a target, the same as the consumers' `targetKey`. */
export function cardTargetKey(t: CardTarget): string {
  return t.kind === 'order'
    ? JSON.stringify(['order', t.scope, t.venue, t.venueOrderId])
    : JSON.stringify(['position', t.scope, t.venue, t.marketId, t.outcome]);
}

/**
 * A reply's target must BE one we asked about. The requested object is
 * returned (not the parsed copy), so the consumer's key always matches.
 */
function echoed(value: unknown, asked: ReadonlyMap<string, CardTarget>): CardTarget {
  const t = object(value);
  const keys = Object.keys(t).sort().join(',');
  if (keys !== 'kind,scope,venue,venueOrderId' && keys !== 'kind,marketId,outcome,scope,venue') return bad();
  const match = asked.get(cardTargetKey(t as unknown as CardTarget));
  return match ?? bad();
}

function revisionFields(r: Record<string, unknown>, minRevision: number) {
  const { revision, updatedAt } = r;
  if (typeof revision !== 'number' || !Number.isSafeInteger(revision) || revision < minRevision) return bad();
  if (revision === 0 ? updatedAt !== null : typeof updatedAt !== 'string' || !ISO.test(updatedAt)) return bad();
  return { revision, updatedAt: updatedAt as string | null };
}

function noteRecord(value: unknown, asked: ReadonlyMap<string, CardTarget>, minRevision = 1): CardNoteRecord {
  const r = object(value);
  if (!(r.note === null || typeof r.note === 'string')) return bad();
  return { target: echoed(r.target, asked), note: r.note as string | null, ...revisionFields(r, minRevision) };
}

function assignment(value: unknown, asked: ReadonlyMap<string, CardTarget>, minRevision = 1): CardSourceAssignment {
  const r = object(value);
  if (!(r.sourceId === null || (typeof r.sourceId === 'string' && UUID.test(r.sourceId)))) return bad();
  return { target: echoed(r.target, asked), sourceId: r.sourceId as string | null, ...revisionFields(r, minRevision) };
}

function source(value: unknown): CardSource {
  const s = object(value);
  if (typeof s.id !== 'string' || !UUID.test(s.id) || typeof s.name !== 'string' || s.name === '') return bad();
  return { id: s.id, name: s.name };
}

const askedMap = (targets: readonly CardTarget[]) => new Map(targets.map((t) => [cardTargetKey(t), t]));

function unavailableList(value: unknown, asked: ReadonlyMap<string, CardTarget>): CardUnavailableTarget[] {
  if (!Array.isArray(value)) return bad();
  return value.map((entry) => {
    const u = object(entry);
    if (u.reason !== 'no_exact_account_identity' || Object.keys(u).length !== 2) return bad();
    return { target: echoed(u.target, asked), reason: u.reason };
  });
}

/** Each asked target appears at most once across records and unavailable entries. */
function uniqueRows<T extends { target: CardTarget }>(rows: T[], unavailable: CardUnavailableTarget[] = []): T[] {
  const keys = [...rows, ...unavailable].map((r) => cardTargetKey(r.target));
  if (new Set(keys).size !== keys.length) return bad();
  return rows;
}

export type CardAnnotationClient = ReturnType<typeof createCardAnnotationClient>;

export function createCardAnnotationClient(transport: CardAnnotationTransport) {
  /** One request; resolves the parsed JSON body of a 2xx, throws CardAnnotationClientError otherwise. */
  async function request(path: string, method: 'GET' | 'POST', payload?: unknown): Promise<Record<string, unknown>> {
    const epoch = transport.epoch();
    const stale = () => {
      if (transport.epoch() !== epoch) throw new CardAnnotationClientError('CARD_CLIENT_CONTEXT_CHANGED');
    };
    let response: Response;
    try {
      response = await transport.send(BASE + path, {
        method,
        cache: 'no-store',
        redirect: 'error',
        signal: AbortSignal.timeout(TIMEOUT_MS),
        ...(payload === undefined ? {} : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload) }),
      });
    } catch {
      stale();
      throw new CardAnnotationClientError('CARD_CLIENT_UNAVAILABLE');
    }
    stale();
    let body: unknown;
    try { body = await response.json(); } catch { body = null; }
    stale();
    if (response.ok) return object(body);
    const code = body && typeof body === 'object' && CODE.test(String((body as { code?: unknown }).code))
      ? String((body as { code: string }).code) : 'CARD_CLIENT_UNAVAILABLE';
    throw new CardAnnotationClientError(code, response.status, body);
  }

  return {
    async readScope(venues: readonly CardVenue[]): Promise<CardScopeResult> {
      const b = await request(`/scope?venues=${encodeURIComponent(venues.join(','))}`, 'GET');
      if (!(b.scope === null || (typeof b.scope === 'string' && SCOPE.test(b.scope)))) return bad();
      const raw = object(b.venues);
      const out: CardScopeResult['venues'] = {};
      for (const [venue, value] of Object.entries(raw)) {
        const v = object(value);
        if (!VENUES.includes(venue as CardVenue) || typeof v.available !== 'boolean') return bad();
        if (v.available) out[venue as CardVenue] = { available: true };
        else if (v.reason === 'no_exact_account_identity') {
          out[venue as CardVenue] = { available: false, reason: v.reason };
        } else return bad();
      }
      return { scope: b.scope as string | null, venues: out };
    },

    async readNotes(targets: readonly CardTarget[]): Promise<{ records: CardNoteRecord[]; unavailable: CardUnavailableTarget[] }> {
      const asked = askedMap(targets);
      const b = await request('/notes/read', 'POST', { targets });
      if (!Array.isArray(b.records)) return bad();
      const unavailable = unavailableList(b.unavailable, asked);
      return { records: uniqueRows(b.records.map((r) => noteRecord(r, asked)), unavailable), unavailable };
    },

    /**
     * A manager or admin READS a trader's notes in their own org (read-only; there is no write
     * counterpart). Every target carries the `trader-view` scope; the server scopes by the signed-in
     * viewer's live org and role, and resolves the notes as the trader.
     */
    async readTraderNotes(traderId: string, targets: readonly CardTarget[]): Promise<{ records: CardNoteRecord[]; unavailable: CardUnavailableTarget[] }> {
      if (!UUID.test(traderId) || targets.some((t) => t.scope !== TRADER_VIEW_SCOPE)) throw new CardAnnotationClientError('CARD_CLIENT_INVALID_REQUEST');
      const asked = askedMap(targets);
      const b = await request(`/oversight/traders/${traderId.toLowerCase()}/notes/read`, 'POST', { targets });
      if (!Array.isArray(b.records)) return bad();
      const unavailable = unavailableList(b.unavailable, asked);
      return { records: uniqueRows(b.records.map((r) => noteRecord(r, asked)), unavailable), unavailable };
    },

    async saveNote(target: CardTarget, note: string | null, expectedRevision: number): Promise<CardNoteRecord> {
      const b = await request('/notes/save', 'POST', { target, note, expectedRevision });
      return noteRecord(b.record, askedMap([target]));
    },

    async listSources(): Promise<CardSource[]> {
      const b = await request('/sources', 'GET');
      if (!Array.isArray(b.sources)) return bad();
      const sources = b.sources.map(source);
      if (new Set(sources.map((s) => s.id)).size !== sources.length) return bad();
      return sources;
    },

    async createSource(name: string): Promise<CardSourceCreated> {
      const b = await request('/sources', 'POST', { name });
      if (typeof b.reused !== 'boolean') return bad();
      return { source: source(b.source), reused: b.reused };
    },

    async readAssignments(targets: readonly CardTarget[]): Promise<{ assignments: CardSourceAssignment[]; unavailable: CardUnavailableTarget[] }> {
      const asked = askedMap(targets);
      const b = await request('/assignments/read', 'POST', { targets });
      if (!Array.isArray(b.assignments)) return bad();
      const unavailable = unavailableList(b.unavailable, asked);
      return { assignments: uniqueRows(b.assignments.map((r) => assignment(r, asked)), unavailable), unavailable };
    },

    async assign(target: CardTarget, sourceId: string | null, expectedRevision: number): Promise<CardSourceAssignment> {
      const b = await request('/assignments/assign', 'POST', { target, sourceId, expectedRevision });
      return assignment(b.assignment, askedMap([target]));
    },

    /** Parse a 409's canonical record against the target that was written. */
    conflictNote(error: CardAnnotationClientError, target: CardTarget): CardNoteRecord | null {
      try { return noteRecord(object(error.body).latest, askedMap([target]), 0); } catch { return null; }
    },
    conflictAssignment(error: CardAnnotationClientError, target: CardTarget): CardSourceAssignment | null {
      try { return assignment(object(error.body).latest, askedMap([target]), 0); } catch { return null; }
    },
  };
}
